/**
 * Extension Lane Panel — the resolved app/org extension subscription, per-member personal
 * subscriptions (ADR-021 user lane), and the read-only team pool (ADR-021 team lane).
 */
import React, { useMemo, useState } from 'react'
import { CreditCard, CalendarToday, OpenInNew, Warning, Delete, CardGiftcard, Person, Groups } from '@mui/icons-material'
import { SubscriptionPlan } from '@numeo/types-core'
import dayjs from 'dayjs'
import { CustomerDetail as CustomerDetailType } from './types'
import GiveTrialDialog from './GiveTrialDialog'

interface ExtensionLanePanelProps {
    appId: string
    detail: CustomerDetailType
    onOpenCancelDialog: () => void
    onOpenDeleteDialog: () => void
    onSuccess: (message: string) => void
    onRefresh: () => Promise<void>
}

function planBadgeClasses(plan: SubscriptionPlan): string {
    if (plan === SubscriptionPlan.PRO) return 'bg-blue-100 text-blue-700'
    if (plan === SubscriptionPlan.ULTRA) return 'bg-violet-100 text-violet-700'
    return 'bg-gray-100 text-gray-600'
}

function statusBadgeClasses(status: string): string {
    if (status === 'active') return 'bg-emerald-50 text-emerald-700'
    if (status === 'trialing') return 'bg-amber-50 text-amber-700'
    return 'bg-red-50 text-red-700'
}

export default function ExtensionLanePanel({ appId, detail, onOpenCancelDialog, onOpenDeleteDialog, onSuccess, onRefresh }: ExtensionLanePanelProps) {
    const sub = detail.subscription
    // Effective = best-of {personal, team seat, app lane} — what the customer actually gets.
    const effective =
        detail.effective ??
        (sub
            ? { plan: sub.plan, status: sub.status as string, source: 'APP' as const }
            : { plan: 'FREE' as SubscriptionPlan, status: 'active', source: 'FREE' as const })
    const team = detail.teamSubscription
    const users = useMemo(() => detail.users || [], [detail.users])
    const ownerUser = useMemo(() => users.find((u) => u.email === detail.ownerEmail) || users[0], [users, detail.ownerEmail])
    const [memberUserId, setMemberUserId] = useState('')
    const [trialDialogOpen, setTrialDialogOpen] = useState(false)

    const selectedUserId = memberUserId || ownerUser?.userId || ''
    const selectedUser = users.find((u) => u.userId === selectedUserId)
    const personal = selectedUser?.extensionSubscription

    // Seats: with a team subscription the truth is the POOL (active grants / purchased),
    // never the legacy flat lane's `seats` (which still says 1 for migrated team orgs).
    const activeGrantCount = (team?.grants ?? []).filter((g) => !g.endsAt || new Date(g.endsAt) > new Date()).length
    const purchasedSeats = (team?.seats ?? []).reduce((sum, s) => sum + (s.purchased || 0), 0)
    const seatsUsed = team ? activeGrantCount : detail.billableUsers
    const seatsTotal = team ? purchasedSeats : (sub?.seats ?? 1)

    // The personal-subscription picker is only useful when someone actually HAS a personal
    // user-lane sub — an all-empty dropdown is noise (the Give Trial dialog has its own picker).
    const anyPersonalSubs = users.some((u) => u.extensionSubscription)

    // Dates must follow the WINNING lane, like the plan badge — showing the stale app-lane
    // trial window under an active team seat reads as wrong data.
    const ownerPersonal = ownerUser?.extensionSubscription
    const winnerDates =
        effective.source === 'TEAM_GRANT'
            ? { periodLabel: 'Renews', periodStart: null, periodEnd: team?.currentPeriodEnd ?? null, trialEnd: null }
            : effective.source === 'PERSONAL'
              ? {
                    periodLabel: 'Current Period',
                    periodStart: null,
                    periodEnd: ownerPersonal?.currentPeriodEnd ?? null,
                    trialEnd: ownerPersonal?.trialEnd ?? null,
                }
              : {
                    periodLabel: 'Current Period',
                    periodStart: sub?.currentPeriodStart ?? null,
                    periodEnd: sub?.currentPeriodEnd ?? null,
                    trialEnd: sub?.trialEnd ?? null,
                }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                    <CreditCard sx={{ fontSize: 14 }} className="text-blue-600" />
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-900">Extension Subscription</p>
                    <p className="text-xs text-gray-400">Resolved plan, per-member personal subscriptions, and the team pool</p>
                </div>
            </div>
            <div className="p-5">
                {sub ? (
                    <>
                        <div className="grid grid-cols-4 gap-4 mb-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Effective Plan</p>
                                <span className={`text-xs font-bold px-2.5 py-0.5 rounded ${planBadgeClasses(effective.plan)}`}>{effective.plan}</span>
                                {effective.source === 'TEAM_GRANT' && <p className="text-[10px] text-gray-400 mt-1">via team seat</p>}
                                {effective.source === 'PERSONAL' && <p className="text-[10px] text-gray-400 mt-1">personal plan</p>}
                                {effective.source === 'APP' && effective.plan !== sub.plan && (
                                    <p className="text-[10px] text-gray-400 mt-1">app subscription</p>
                                )}
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Status</p>
                                <span className={`text-xs px-2.5 py-0.5 rounded ${statusBadgeClasses(effective.status)}`}>{effective.status}</span>
                                {effective.source !== 'APP' && effective.plan !== sub.plan && (
                                    <p className="text-[10px] text-gray-400 mt-1">
                                        app lane: {sub.plan} {sub.status}
                                    </p>
                                )}
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Billing Period</p>
                                <p className="text-sm font-medium text-gray-700">{sub.billingPeriod || '—'}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{team ? 'Team Seats' : 'Seats'}</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-700">
                                        {seatsUsed} / {seatsTotal}
                                    </p>
                                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-indigo-500 rounded-full"
                                            style={{ width: `${Math.min(100, (seatsUsed / (seatsTotal || 1)) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-100">
                            {winnerDates.periodEnd && (
                                <div className="flex items-center gap-2">
                                    <CalendarToday sx={{ fontSize: 13 }} className="text-gray-400" />
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{winnerDates.periodLabel}</p>
                                        <p className="text-sm text-gray-700">
                                            {winnerDates.periodStart ? `${dayjs(winnerDates.periodStart).format('MMM D')} — ` : ''}
                                            {dayjs(winnerDates.periodEnd).format('MMM D, YYYY')}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {winnerDates.trialEnd && (
                                <div className="flex items-center gap-2">
                                    <CalendarToday sx={{ fontSize: 13 }} className="text-gray-400" />
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Trial End</p>
                                        <p className="text-sm text-gray-700">{dayjs(winnerDates.trialEnd).format('MMM D, YYYY')}</p>
                                    </div>
                                </div>
                            )}
                            {sub.stripeCustomerId && (
                                <div className="flex items-center gap-2">
                                    <OpenInNew sx={{ fontSize: 13 }} className="text-gray-400" />
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Stripe Customer</p>
                                        <p className="text-xs font-mono text-indigo-600">{sub.stripeCustomerId}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        {sub.pendingPlanChange && (
                            <div className="mt-3 bg-amber-50 text-amber-700 px-3 py-2 rounded-lg text-sm">
                                Pending plan change to <strong>{sub.pendingPlanChange.targetPlan}</strong> on{' '}
                                {dayjs(sub.pendingPlanChange.effectiveDate).format('MMM D, YYYY')}
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-gray-400 mb-4">No resolved subscription for this application</p>
                )}

                {/* Personal (user-lane) subscriptions — member picker, owner first.
                    Hidden when no member has one: an all-empty dropdown is noise, and the
                    Give Trial dialog carries its own member picker. */}
                {users.length > 0 && anyPersonalSubs && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                            <Person sx={{ fontSize: 14 }} className="text-indigo-600" />
                            <p className="text-xs font-semibold text-gray-600">Personal Subscription</p>
                            <select
                                value={selectedUserId}
                                onChange={(e) => setMemberUserId(e.target.value)}
                                className="h-8 text-xs px-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 max-w-[240px]"
                            >
                                {users.map((u) => (
                                    <option key={u.userId} value={u.userId}>
                                        {u.email}
                                        {u.email === detail.ownerEmail ? ' (owner)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {personal ? (
                            <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-md bg-gray-50 text-xs">
                                <span className={`font-bold px-2 py-0.5 rounded ${planBadgeClasses(personal.plan)}`}>{personal.plan}</span>
                                <span className={`px-2 py-0.5 rounded ${statusBadgeClasses(personal.status)}`}>{personal.status}</span>
                                {personal.billingPeriod && <span className="text-gray-400">{personal.billingPeriod}</span>}
                                {personal.trialEnd && <span className="text-amber-600">trial ends {dayjs(personal.trialEnd).format('MMM D, YYYY')}</span>}
                                {personal.currentPeriodEnd && (
                                    <span className="text-gray-400">
                                        {personal.cancelAtPeriodEnd ? 'ends' : 'renews'} {dayjs(personal.currentPeriodEnd).format('MMM D, YYYY')}
                                    </span>
                                )}
                                {personal.isGift && (
                                    <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                                        <CardGiftcard sx={{ fontSize: 10 }} /> Gift
                                    </span>
                                )}
                                {personal.stripeSubscriptionId && <span className="font-mono text-[10px] text-gray-400">{personal.stripeSubscriptionId}</span>}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400 px-3 py-2 rounded-md bg-gray-50">No personal extension subscription</p>
                        )}
                    </div>
                )}

                {/* Team pool (org lane) — read-only; seats are managed from the product UI */}
                {team && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                            <Groups sx={{ fontSize: 14 }} className="text-violet-600" />
                            <p className="text-xs font-semibold text-gray-600">Team Subscription</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${statusBadgeClasses(team.status)}`}>{team.status}</span>
                            {team.currentPeriodEnd && (
                                <span className="text-xs text-gray-400">renews {dayjs(team.currentPeriodEnd).format('MMM D, YYYY')}</span>
                            )}
                            <span className="text-[10px] text-gray-400 ml-auto">read-only — seats are managed in the product</span>
                        </div>
                        {team.seats.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {team.seats.map((s) => (
                                    <span key={s.plan} className={`text-xs px-2 py-1 rounded ${planBadgeClasses(s.plan)}`}>
                                        {s.plan} × {s.purchased}
                                    </span>
                                ))}
                            </div>
                        )}
                        {team.grants.length > 0 ? (
                            <div className="space-y-1">
                                {team.grants.map((g) => {
                                    // A grant past its endsAt is REVOKED — the member is not on this plan
                                    // anymore (the pool already excludes it); render it as history, not as
                                    // a live seat. "invited, not joined" only when no member with that
                                    // email actually exists (the grant's userId back-link can lag a join).
                                    const ended = !!g.endsAt && new Date(g.endsAt) <= new Date()
                                    const member = users.find((u) => u.email.toLowerCase() === g.email.toLowerCase())
                                    const joined = !!g.userId || !!member
                                    // After a revoke, show what the member resolves to NOW (personal sub,
                                    // another grant, app lane, or FREE) — the struck badge alone only says
                                    // what they lost, not what they currently have.
                                    const current = ended ? member?.effective : undefined
                                    return (
                                        <div
                                            key={g.email}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 text-xs ${ended ? 'opacity-60' : ''}`}
                                        >
                                            <span className="text-gray-700 truncate">{g.email}</span>
                                            <span
                                                className={`font-bold px-2 py-0.5 rounded ${ended ? 'bg-gray-100 text-gray-500 line-through' : planBadgeClasses(g.plan)}`}
                                            >
                                                {g.plan}
                                            </span>
                                            {ended && <span className="text-gray-500">revoked</span>}
                                            {current && (
                                                <span className="flex items-center gap-1">
                                                    <span className="text-gray-400">now:</span>
                                                    <span className={`font-bold px-2 py-0.5 rounded ${planBadgeClasses(current.plan)}`}>{current.plan}</span>
                                                    <span className="text-gray-400">{current.status}</span>
                                                </span>
                                            )}
                                            {!ended && !joined && <span className="text-amber-600">invited, not joined</span>}
                                            <span className="text-gray-400 ml-auto">assigned {dayjs(g.assignedAt).format('MMM D, YYYY')}</span>
                                            {g.endsAt && (
                                                <span className={ended ? 'text-gray-500' : 'text-red-600'}>
                                                    {ended ? 'ended' : 'ends'} {dayjs(g.endsAt).format('MMM D, YYYY')}
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400">No team grants</p>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                    <button
                        onClick={() => setTrialDialogOpen(true)}
                        className="h-8 px-4 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                    >
                        <CardGiftcard sx={{ fontSize: 12 }} /> Give Trial
                    </button>
                    {sub && sub.plan !== SubscriptionPlan.FREE && sub.stripeSubscriptionId && !sub.cancelAtPeriodEnd && (
                        <button
                            onClick={onOpenCancelDialog}
                            className="h-8 px-3 text-xs text-red-600 border border-red-200 rounded-md hover:bg-red-50 flex items-center gap-1.5 transition-colors"
                        >
                            <Warning sx={{ fontSize: 12 }} /> Cancel Subscription
                        </button>
                    )}
                    {sub?.cancelAtPeriodEnd && <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-md">Will cancel at period end</span>}
                    {sub?.stripeCustomerId && (
                        <button
                            onClick={onOpenDeleteDialog}
                            className="h-8 px-3 text-xs text-red-600 border border-red-200 rounded-md hover:bg-red-50 flex items-center gap-1.5 transition-colors"
                        >
                            <Delete sx={{ fontSize: 12 }} /> Delete Stripe Customer
                        </button>
                    )}
                </div>
            </div>

            <GiveTrialDialog
                open={trialDialogOpen}
                lane="extension"
                appId={appId}
                users={users}
                defaultUserId={ownerUser?.userId}
                ownerEmail={detail.ownerEmail}
                onClose={() => setTrialDialogOpen(false)}
                onSuccess={onSuccess}
                onRefresh={onRefresh}
            />
        </div>
    )
}
